/** One search across rooms, circles, Direct, announcements, files and what was said in the replays. */
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import type { TalkHit, TalkMoment, TalkSearchResponse } from "../../shared/talk";
import { Button } from "../components/ui/Button";
import { Face, FaceStack } from "../components/ui/Face";
import { Icon } from "../components/ui/Icon";
import { Sheet } from "../components/ui/Sheet";
import { Toggle } from "../components/ui/Toggle";
import { useToast } from "../components/ui/Toast";
import { api } from "../lib/api";
import { facesOf, FileTile, Marked } from "./parts";
import { Nav } from "./phone";
import { RoomLine } from "./PhoneRooms";
import { hitPath, searchPath, talkPath } from "./places";
import { ShutRoom } from "./RailRooms";
import { useTalkContext } from "./TalkLayout";
import { useRemote } from "./useTalk";
import { firstName, lineWhen } from "./when";

type Asked = NonNullable<TalkSearchResponse["asked"]>;

const secs = (n: number) => `${Math.floor(n / 60)}:${String(Math.floor(n % 60)).padStart(2, "0")}`;
const momentPath = (m: TalkMoment) => (m.liveSessionId ? `/rooms/${m.webinarId}/sessions/${m.liveSessionId}` : `/rooms/${m.webinarId}`);

/** Writes one message to each of the people who asked, privately, as chat and by email. */
function PrivatelySheet({ asked, onClose }: { asked: Asked; onClose: () => void }) {
  const toast = useToast();
  const [body, setBody] = useState("");
  const [email, setEmail] = useState(true);
  const [subject, setSubject] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!body.trim() || busy) return;
    setBusy(true);
    try {
      const r = await api.talk.sendPrivately({ to: { registrantIds: asked.map((a) => a.registrantId) }, body: body.trim(), email, subject: email ? subject.trim() || undefined : undefined, requestId: crypto.randomUUID() });
      toast({ text: `Sent to ${r.sent} ${r.sent === 1 ? "person" : "people"}`, icon: "send" });
      onClose();
    } catch {
      toast({ text: "We couldn't send that. Try again.", icon: "info" });
      setBusy(false);
    }
  };
  return (
    <Sheet
      open
      onClose={onClose}
      title="Message each privately"
      sub={`${asked.length} ${asked.length === 1 ? "person gets" : "people each get"} it in their own Direct thread`}
      footer={
        <>
          <Button onClick={onClose}>Not now</Button>
          <Button variant="p" icon="send" disabled={busy || !body.trim()} onClick={() => void send()}>
            Send
          </Button>
        </>
      }
    >
      <div className="sheet-b hp-ctl">
        <label className="field">
          <span>Your message</span>
          <textarea className="inp" rows={5} value={body} onChange={(e) => setBody(e.target.value)} />
        </label>
        <div className="set">
          <span>
            <b>Also email it</b>
          </span>
          <Toggle on={email} onChange={setEmail} label="Also email it" />
        </div>
        {email ? (
          <label className="field">
            <span>Subject</span>
            <input className="inp" value={subject} onChange={(e) => setSubject(e.target.value)} />
          </label>
        ) : null}
      </div>
    </Sheet>
  );
}

export function SearchScreen() {
  const [params] = useSearchParams();
  const q = (params.get("q") ?? "").trim();
  const ctx = useTalkContext();
  const phone = ctx.lay === "phone";
  const upright = ctx.lay === "upright";
  const navigate = useNavigate();
  const [writing, setWriting] = useState(false);
  const [typed, setTyped] = useState(q);
  const { data, phase } = useRemote(q.length >= 2 ? `talk:search:${q.toLowerCase()}` : null, () => api.talk.search(q));
  const found = data && data.q.toLowerCase() === q.toLowerCase() ? data : null;
  const asked = found?.asked ?? [];

  const startCircle = () => {
    const rooms = new Set<string>();
    for (const h of found?.messages ?? []) {
      if (h.place.kind === "everyone" || h.place.kind === "announce") rooms.add(h.place.id);
      if (h.place.kind === "circle") {
        const r = ctx.rail?.rooms.find((x) => x.circles.some((c) => c.circleId === h.place.id));
        if (r) rooms.add(r.webinarId);
      }
    }
    const ids = [...rooms];
    ctx.startCircle(ids.length === 1 ? (ids[0] ?? null) : null, undefined, { registrantIds: asked.map((a) => a.registrantId), faces: asked, roomIds: ids });
  };

  const askedCard =
    asked.length > 0 ? (
      <div className="side-card">
        <small>Asked about {q}</small>
        <b>
          {asked.length} {asked.length === 1 ? "person" : "people"}
        </b>
        {asked.map((w) => (
          <div key={w.registrantId} className="mem">
            <Face name={w.name} photo={w.photoUrl} size={30} />
            <span className="mem-t">
              <b>{w.name}</b>
              <span>{w.tag}</span>
            </span>
          </div>
        ))}
        <span className="side-card-acts">
          <Button size="s" icon="chat" onClick={() => setWriting(true)}>
            Message each privately
          </Button>
          <Button size="s" icon="target" onClick={startCircle}>
            Start a circle with them
          </Button>
        </span>
      </div>
    ) : null;

  const rooms = found?.rooms ?? [];
  const total = found ? rooms.length + found.moments.length + found.messages.length + found.files.length : 0;
  const pic = (m: TalkMoment) => (
    <span className={m.faces?.length ? "rs-pic call" : "rs-pic"}>
      {m.faces?.length ? m.faces.slice(0, 4).map((f, i) => (f.photoUrl ? <img key={i} src={f.photoUrl} alt="" /> : <span key={i} />)) : m.posterUrl ? <img src={m.posterUrl} alt="" /> : null}
      <i>{secs(m.atSec)}</i>
    </span>
  );
  const said = (m: TalkMoment) => (
    <span className="rs-t">
      <b>{m.title}</b>
      <p>
        <Marked text={m.text} word={q} />
      </p>
    </span>
  );
  const msgBody = (h: TalkHit, size: number) => (
    <>
      <Face name={h.who.name} photo={h.who.photoUrl} size={size} />
      <span className="rs-t">
        <b>
          {h.who.kind === "host" ? "You" : h.who.name}
          <small>
            {h.where} · {lineWhen(h.createdAt)}
          </small>
        </b>
        <p>
          <Marked text={h.text} word={q} />
        </p>
      </span>
    </>
  );
  const fileRows = found?.files.map((f) => (
    <a key={f.key} className="rs-msg rs-file" href={f.url} target="_blank" rel="noreferrer noopener">
      <FileTile f={f} />
      <span className="rs-t">
        <b>
          {f.name}
          <small>
            {f.where} · {firstName(f.by.name)}
          </small>
        </b>
      </span>
    </a>
  ));
  const none = found && total === 0 ? <p className="empty-note">Nothing found for "{q}".</p> : null;
  const failed = !found && phase === "error" ? <p className="empty-note">We couldn't search just now. Try again in a moment.</p> : null;
  const results = found ? (
    <>
      {rooms.length ? <p className="rs-h">Rooms · {rooms.length}</p> : null}
      {rooms.map((r) => (
        <ShutRoom key={r.webinarId} room={r} />
      ))}
      {found.moments.length ? <p className="rs-h">Said in a replay · {found.moments.length}</p> : null}
      {found.moments.map((m) => (
        <div key={`${m.liveSessionId}-${m.atSec}`} className="rs-moment">
          {pic(m)}
          {said(m)}
          <Button size="s" icon="play" onClick={() => navigate(momentPath(m))}>
            Play from {secs(m.atSec)}
          </Button>
        </div>
      ))}
      {found.messages.length ? <p className="rs-h">Messages · {found.messages.length}</p> : null}
      {found.messages.map((h) => (
        <button key={h.id} type="button" className="rs-msg" onClick={() => navigate(hitPath(h.place, h.id))}>
          {msgBody(h, 34)}
        </button>
      ))}
      {found.files.length ? <p className="rs-h">Files · {found.files.length}</p> : null}
      {fileRows}
      {none}
    </>
  ) : (
    failed
  );
  const phoneResults = found ? (
    <>
      {rooms.length ? <p className="hp-sub big">Rooms · {rooms.length}</p> : null}
      {rooms.length ? (
        <section className="hp-card">
          {rooms.map((r) => (
            <RoomLine key={r.webinarId} room={r} />
          ))}
        </section>
      ) : null}
      {found.moments.length ? <p className="hp-sub big">Said in a replay · {found.moments.length}</p> : null}
      {found.moments.map((m) => (
        <button key={`${m.liveSessionId}-${m.atSec}`} type="button" className="hp-moment" onClick={() => navigate(momentPath(m))}>
          {pic(m)}
          {said(m)}
        </button>
      ))}
      {found.messages.length ? <p className="hp-sub big">Messages · {found.messages.length}</p> : null}
      {found.messages.length ? (
        <section className="hp-card">
          {found.messages.map((h) => (
            <button key={h.id} type="button" className="rs-msg" onClick={() => navigate(hitPath(h.place, h.id))}>
              {msgBody(h, 32)}
            </button>
          ))}
        </section>
      ) : null}
      {found.files.length ? <p className="hp-sub big">Files · {found.files.length}</p> : null}
      {found.files.length ? <section className="hp-card">{fileRows}</section> : null}
      {none}
    </>
  ) : (
    failed
  );
  const sheet = writing ? <PrivatelySheet asked={asked} onClose={() => setWriting(false)} /> : null;

  if (phone) {
    return (
      <div className="hp">
        <Nav back="Back" title="Search" />
        <div className="hp-scroll">
          <div className="hp-page">
            <label className={q ? "hp-q in on" : "hp-q in"}>
              <Icon name="search" />
              <input
                autoFocus={!q}
                aria-label="Search"
                placeholder="Search messages, files, people"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && typed.trim().length >= 2 && navigate(searchPath(typed.trim()), { replace: true })}
              />
              {typed ? (
                <button type="button" className="rail-qx" aria-label="Clear the search" onClick={() => (setTyped(""), navigate(`${talkPath}/search`, { replace: true }))}>
                  <Icon name="x" />
                </button>
              ) : null}
            </label>
            {asked.length ? (
              <section className="hp-asked">
                <FaceStack people={facesOf(asked)} size={32} />
                <span>
                  <b>
                    {asked.length} {asked.length === 1 ? "person asked" : "people asked"} about {q}
                  </b>
                  <small>{asked.map((a) => firstName(a.name)).join(", ")}</small>
                </span>
                <span className="hp-asked-b">
                  <button type="button" className="fchip" onClick={() => setWriting(true)}>
                    Message each privately
                  </button>
                  <button type="button" className="fchip" onClick={startCircle}>
                    Start a circle
                  </button>
                </span>
              </section>
            ) : null}
            {phoneResults}
          </div>
        </div>
        {sheet}
      </div>
    );
  }
  return (
    <>
      <main className="main">
        <div className="mh">
          <span className="mh-circle">
            <Icon name="search" />
          </span>
          <div className="mh-t">
            <h2>{q ? `"${q}"` : "Search"}</h2>
            <p>{found ? `${total} ${total === 1 ? "result" : "results"} in your rooms, circles and Direct` : "Type at least two letters"}</p>
          </div>
        </div>
        <div className="results">
          {upright ? askedCard : null}
          {results}
        </div>
      </main>
      <aside className="side">{askedCard}</aside>
      {sheet}
    </>
  );
}
